import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useGetSenderIdentityQuery,
    useRemoveSenderIdentityMutation,
    useSetSenderIdentityMutation,
} from '../../api/account/accountApiSlice';
import CustomButton from '../../components/CustomButton';
import InputField from '../../components/ui/InputField';

/** The server's refusal reasons, each with its own sentence (#2255). */
const REASON_KEYS: Record<string, string> = {
    invalid: 'account.sender.error_invalid',
    freemail: 'account.sender.error_freemail',
    reserved: 'account.sender.error_reserved',
    domain_taken: 'account.sender.error_domain_taken',
    ses_not_permitted: 'account.sender.error_not_enabled',
};

const reasonOf = (err: unknown): { reason?: string, message?: string } =>
    ((err as { data?: { reason?: string, message?: string } } | null)?.data) ?? {};

/**
 * The tenant's own sender address for customer mails
 * (docs/plans/tenant-sender-domain.md). The owner enters an address, the
 * screen shows the DNS records SES issued for its domain, and mail switches
 * to the address only once SES has verified it — until then everything keeps
 * going out from the current sender, so nothing is lost while DNS settles.
 */
const SenderAddressSection = () => {
    const { t } = useTranslation();
    const { data, isFetching, isError, refetch } = useGetSenderIdentityQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const [setSender, { isLoading: saving }] = useSetSenderIdentityMutation();
    const [removeSender, { isLoading: removing }] = useRemoveSenderIdentityMutation();
    const [address, setAddress] = useState('');
    const [error, setError] = useState<string | null>(null);

    const sender = data?.sender ?? null;

    const explain = (err: unknown) => {
        const { reason, message } = reasonOf(err);
        setError(reason && REASON_KEYS[reason] ? t(REASON_KEYS[reason]) : (message ?? t('account.sender.error_generic')));
    };

    const handleStart = async () => {
        setError(null);
        try {
            await setSender({ address: address.trim() }).unwrap();
            setAddress('');
        } catch (err) {
            explain(err);
        }
    };

    const handleRemove = async () => {
        setError(null);
        try {
            await removeSender().unwrap();
        } catch (err) {
            explain(err);
        }
    };

    return (
        <section id={'sender-address'} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <h2 className={'text-lg font-semibold text-foreground mb-1'}>
                {t('account.sender.title')}
            </h2>
            <p className={'text-sm text-foreground/60 mb-5'}>
                {t('account.sender.description')}
            </p>

            {isError && (
                <p className={'text-sm text-error mb-3'}>{t('account.sender.error_load')}</p>
            )}
            {data?.checkError && (
                <p className={'text-sm text-error mb-3'}>
                    {t(REASON_KEYS[data.checkError] ?? 'account.sender.error_generic')}
                </p>
            )}

            {sender && (
                <div className={'flex flex-col gap-3 mb-5'}>
                    <p className={'text-sm text-foreground'}>
                        <span className={'font-medium'}>{sender.address}</span>
                        {' · '}
                        {t(`account.sender.status_${sender.status}`)}
                    </p>
                    {sender.status !== 'verified' && (
                        <>
                            <p className={'text-xs text-foreground/60'}>
                                {t('account.sender.dns_intro', { domain: sender.domain })}
                            </p>
                            <div className={'overflow-x-auto'}>
                                <table className={'w-full text-sm'}>
                                    <thead>
                                        <tr className={'border-b border-foreground/10'}>
                                            <th className={'text-left py-2 pr-4 text-foreground/60 font-medium'}>
                                                {t('account.sender.dns_type')}
                                            </th>
                                            <th className={'text-left py-2 pr-4 text-foreground/60 font-medium'}>
                                                {t('account.sender.dns_name')}
                                            </th>
                                            <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                                {t('account.sender.dns_value')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sender.dnsRecords.map((record) => (
                                            <tr key={record.name} className={'border-b border-foreground/5'}>
                                                <td className={'py-2 pr-4 text-foreground'}>{record.type}</td>
                                                <td className={'py-2 pr-4 text-foreground break-all select-all'}>{record.name}</td>
                                                <td className={'py-2 text-foreground break-all select-all'}>{record.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className={'text-xs text-foreground/60'}>{t('account.sender.dns_hint')}</p>
                            <div>
                                <CustomButton
                                    title={t('account.sender.check_now')}
                                    isFilled={false}
                                    loading={isFetching}
                                    onClick={() => refetch()}
                                />
                            </div>
                        </>
                    )}
                    {sender.status === 'verified' && (
                        <p className={'text-xs text-foreground/60'}>{t('account.sender.verified_hint')}</p>
                    )}
                    <div>
                        <CustomButton
                            title={t('account.sender.remove')}
                            isFilled={false}
                            loading={removing}
                            onClick={handleRemove}
                        />
                    </div>
                </div>
            )}

            <div className={'flex flex-col gap-3 max-w-md'}>
                <InputField
                    label={sender ? t('account.sender.change_label') : t('account.sender.address_label')}
                    value={address}
                    onChange={(value) => { setAddress(value); setError(null); }}
                    placeholder={t('account.sender.address_placeholder')}
                />
                <div>
                    <CustomButton
                        title={t('account.sender.start')}
                        loading={saving}
                        disabled={address.trim() === ''}
                        onClick={handleStart}
                    />
                </div>
                {error && <p className={'text-sm text-error'}>{error}</p>}
            </div>
        </section>
    );
};

export default SenderAddressSection;
